import React from 'react';
import axios from 'axios';
import { ApiRoute } from '@routes/apiRoutes';
import { ResponseWrapper } from '@models/api/responseWrapper';
import { ToastConstants } from '@constants/toastConstants';
import { shouldSuppressToaster } from '../utils/toasterConfig/toasterConfig';
import { ServiceMaintenanceStatusCodes } from '@enums/httpStatusCodes';
import { NetworkErrorCode } from '@enums/networkErrorCodes';
import { NetworkErrorMessage } from '@enums/networkErrorMessages';
import { HttpServiceConstants } from '@constants/httpServiceConstants';
import { showCustomErrorToast } from '@utils/customToast';

const baseURL = import.meta.env.VITE_BASEURL.replace(/\/+$/, '');

const axiosInstance = axios.create({
  baseURL,
  headers: {
    [HttpServiceConstants.headers.contentType]:
      HttpServiceConstants.headers.applicationJson,
  },
  withCredentials: true,
});

let global401Handler: (() => void) | null = null;

let globalServiceMaintenanceHandler: (() => void) | null = null;

export const setGlobal401Handler = (handler: () => void) => {
  global401Handler = handler;
};

export const setGlobalServiceMaintenanceHandler = (handler: () => void) => {
  globalServiceMaintenanceHandler = handler;
};

const shouldRedirectToServiceMaintenance = (error: any): boolean => {
  const status = error.response?.status;
  const errorCode = error.code;
  const errorMessage = error.message?.toLowerCase() || '';

  const isHttpError = Object.values(ServiceMaintenanceStatusCodes).includes(
    status
  );

  const isNetworkError =
    !error.response &&
    (Object.values(NetworkErrorCode).includes(errorCode) ||
      Object.values(NetworkErrorMessage).some(msg =>
        errorMessage.includes(msg)
      ));

  return isHttpError || isNetworkError;
};

// A file response arrives as a Blob, so it carries no { success, error } envelope to inspect.
// The API answers a handled failure with HTTP 200 and the envelope regardless, so a JSON body
// on a file request is an error, not the file - unwrap it and reject with the server's message.
const readErrorFromBlob = async (
  blob: Blob
): Promise<ResponseWrapper | null> => {
  try {
    return JSON.parse(await blob.text()) as ResponseWrapper;
  } catch {
    return null;
  }
};

// Rejects with an Error, not the bare string the JSON path uses: a file request's callers
// read error.message to toast it themselves.
const rejectWithEnvelopeMessage = (url: string, message?: string) => {
  const error = message || HttpServiceConstants.messages.requestFailed;
  if (!shouldSuppressToaster(url)) {
    showCustomErrorToast({
      message: React.createElement(
        'div',
        { style: { paddingLeft: ToastConstants.contentPaddingLeft } },
        error
      ),
    });
  }
  return Promise.reject(new Error(error));
};

axiosInstance.interceptors.response.use(
  async response => {
    if (
      response.config.responseType === HttpServiceConstants.responseTypes.blob
    ) {
      const contentType =
        response.headers[
          HttpServiceConstants.headers.contentType.toLowerCase()
        ]?.toString() ?? '';
      if (!contentType.includes(HttpServiceConstants.headers.applicationJson)) {
        return response;
      }

      const wrapper = await readErrorFromBlob(response.data as Blob);
      return rejectWithEnvelopeMessage(
        response.config.url || '',
        wrapper?.error
      );
    }

    if (!response.data.success) {
      if (!shouldSuppressToaster(response.config.url || '')) {
        showCustomErrorToast({
          message: React.createElement(
            'div',
            {
              style: { paddingLeft: ToastConstants.contentPaddingLeft },
            },
            response.data.error || HttpServiceConstants.messages.requestFailed
          ),
        });
      }
      return Promise.reject(
        response.data.error || HttpServiceConstants.messages.requestFailed
      );
    }
    return response;
  },
  async error => {
    // A failed file request carries its envelope as a Blob, so the message has to be read out
    // of it before the generic handling below looks for error.response.data.error.
    if (
      error.config?.responseType === HttpServiceConstants.responseTypes.blob &&
      error.response?.data instanceof Blob
    ) {
      error.response.data = await readErrorFromBlob(error.response.data);
    }

    if (error.response?.status === 401 && global401Handler) {
      global401Handler();
    } else if (
      shouldRedirectToServiceMaintenance(error) &&
      globalServiceMaintenanceHandler
    ) {
      globalServiceMaintenanceHandler();
    }

    // The API wraps handled failures as { success: false, error } with a 400.
    // Keep the AxiosError shape but expose the server's message instead of
    // "Request failed with status code 400".
    const apiError = error.response?.data?.error;
    if (typeof apiError === 'string' && apiError.length > 0) {
      error.message = apiError;
    }

    return Promise.reject(error);
  }
);

export const httpService = {
  get: async <TResponse, TData = unknown, TParams = Record<string, unknown>>(
    url: ApiRoute | string,
    data: TData = null as TData,
    params?: TParams,
    signal?: AbortSignal
  ): Promise<TResponse> => {
    let fullUrl = baseURL + url.toString();

    if (data) {
      const dataType = typeof data;

      if (
        dataType === HttpServiceConstants.dataTypes.number ||
        dataType === HttpServiceConstants.dataTypes.string ||
        dataType === HttpServiceConstants.dataTypes.boolean
      ) {
        fullUrl += `/${data}`;
      } else {
        fullUrl += `?${HttpServiceConstants.url.requestParam}=${encodeURIComponent(JSON.stringify(data))}`;
      }
    }

    const response = await axiosInstance.get<ResponseWrapper>(fullUrl, {
      params,
      signal,
    });

    if (!response.data.success) {
      throw new Error(
        response.data.error || HttpServiceConstants.messages.requestFailed
      );
    }

    return response.data.data as TResponse;
  },

  // Files are fetched as a Blob rather than through the JSON envelope: base64 in JSON costs
  // several times the file's size in memory on both ends and fails outright on large
  // attachments (BUG 21562).
  getBlob: async (
    url: ApiRoute | string,
    signal?: AbortSignal
  ): Promise<Blob> => {
    const response = await axiosInstance.get<Blob>(baseURL + url.toString(), {
      responseType: HttpServiceConstants.responseTypes.blob,
      signal,
    });

    return response.data;
  },

  post: async <TResponse, TData = unknown>(
    url: ApiRoute | string,
    data?: TData,
    signal?: AbortSignal
  ): Promise<TResponse> => {
    const response = await axiosInstance.post<ResponseWrapper>(
      url.toString(),
      data,
      {
        signal,
      }
    );

    if (!response.data.success) {
      throw new Error(
        response.data.error || HttpServiceConstants.messages.requestFailed
      );
    }

    return response.data.data as TResponse;
  },

  put: async <TResponse, TData = unknown>(
    url: ApiRoute | string,
    data?: TData,
    signal?: AbortSignal
  ): Promise<TResponse> => {
    let fullUrl = baseURL + url.toString();

    if (data) {
      const dataType = typeof data;

      if (
        dataType === HttpServiceConstants.dataTypes.number ||
        dataType === HttpServiceConstants.dataTypes.string ||
        dataType === HttpServiceConstants.dataTypes.boolean
      ) {
        fullUrl += `/${data}`;
      } else {
        fullUrl += `?${HttpServiceConstants.url.requestParam}=${encodeURIComponent(JSON.stringify(data))}`;
      }
    }

    const response = await axiosInstance.put<ResponseWrapper>(fullUrl, data, {
      signal,
    });

    if (!response.data.success) {
      throw new Error(
        response.data.error || HttpServiceConstants.messages.requestFailed
      );
    }

    return response.data.data as TResponse;
  },

  delete: async <TResponse, TData = unknown>(
    url: ApiRoute | string,
    data?: TData
  ): Promise<TResponse> => {
    let fullUrl = baseURL + url.toString();

    if (data) {
      const dataType = typeof data;

      if (
        dataType === HttpServiceConstants.dataTypes.number ||
        dataType === HttpServiceConstants.dataTypes.string ||
        dataType === HttpServiceConstants.dataTypes.boolean
      ) {
        fullUrl += `/${data}`;
      } else {
        fullUrl += `?${HttpServiceConstants.url.requestParam}=${encodeURIComponent(JSON.stringify(data))}`;
      }
    }

    const response = await axiosInstance.delete<ResponseWrapper>(fullUrl);

    if (!response.data.success) {
      throw new Error(
        response.data.error || HttpServiceConstants.messages.requestFailed
      );
    }

    return response.data.data as TResponse;
  },
};
